/**
 * AccountExtractorStep — new Step 1.5 in the FTC Report Builder wizard.
 *
 *   - User can upload many credit-report screenshots (drag, file picker, paste, or
 *     iOS Photos multi-select) — UI cap is 50 to keep memory sane, and the
 *     backend is called in batches of 6 so any single LLM request stays within
 *     token limits. Results are merged into one editable list.
 *   - "Extract Accounts" → for pasted TEXT only, runs a fast client-side regex
 *     parser FIRST (sub-100ms, no network call). For images OR when the text
 *     parser finds nothing, falls back to the backend GPT vision pipeline.
 *   - User can edit any field, change the category, delete, or add accounts.
 *   - "Use these accounts" pushes results back to the parent and skips ahead
 *     to Step 2 with the text-area pre-populated for the existing `parseAccounts`
 *     pipeline.
 *   - "Skip — paste manually" jumps straight to Step 2.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sparkles, Upload, X, Edit2, Trash2, Plus, ChevronRight, SkipForward,
  Loader2, ImageIcon, FileText, ArrowLeft, Copy, RefreshCw, PlusCircle, GripVertical,
} from "lucide-react";
import AccountAbbreviationModal from "./AccountAbbreviationModal";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  AUTO: "Auto",
  CREDIT_CARD: "Credit Card",
  CREDIT_UNION: "Credit Union",
  PERSONAL_LOAN: "Personal Loan",
  STUDENT_LOAN: "Student Loan",
  MORTGAGE: "Mortgage",
  UTILITY: "Utility",
  MEDICAL: "Medical",
  COLLECTION: "Collection",
  OTHER: "Other",
};
const CATEGORY_ORDER = [
  "AUTO", "CREDIT_CARD", "CREDIT_UNION", "PERSONAL_LOAN",
  "STUDENT_LOAN", "MORTGAGE", "UTILITY", "MEDICAL", "COLLECTION", "OTHER",
];

export default function AccountExtractorStep({ onBack, onSkip, onUseAccounts }) {
  const [images, setImages] = useState([]);     // [{ id, dataUrl, name }]
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  // Per-category label overrides — keyed by the internal category code
  // (AUTO, CREDIT_CARD, …). When the user renames "Collection" → "Collections"
  // we persist the override here so every account in that category keeps
  // grouping under the new label. localStorage so renames survive a refresh.
  const [labelOverrides, setLabelOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("extractor:label-overrides") || "{}"); }
    catch { return {}; }
  });
  const [editingCategoryLabel, setEditingCategoryLabel] = useState(null); // category code being renamed
  // Feb 2026 (user request): the Shorten & Merge modal that already runs
  // on the paste-accounts page should also be available here, right after
  // AI extraction, so long account names can be trimmed and duplicate
  // creditors merged before the user even leaves this screen.
  const [abbrevModalOpen, setAbbrevModalOpen] = useState(false);
  const fileRef = useRef(null);

  // Persist label overrides every time they change
  useEffect(() => {
    try { localStorage.setItem("extractor:label-overrides", JSON.stringify(labelOverrides)); }
    catch { /* ignore */ }
  }, [labelOverrides]);

  // Resolve the display label for a category — override if present, else default.
  const labelFor = (cat) => labelOverrides[cat] || CATEGORY_LABELS[cat] || cat;

  const renameCategory = (cat, newLabel) => {
    const cleaned = String(newLabel || "").trim();
    setLabelOverrides((prev) => {
      const next = { ...prev };
      if (!cleaned || cleaned.toUpperCase() === (CATEGORY_LABELS[cat] || "").toUpperCase()) {
        // Resetting to default → drop the override entirely.
        delete next[cat];
      } else {
        next[cat] = cleaned;
      }
      return next;
    });
  };

  const MAX_IMAGES = 50;
  const BATCH_SIZE = 6; // server cap is 12/call; we send 6 to leave headroom for token bursts

  const addFiles = (files) => {
    if (!files || !files.length) return;
    const allowed = Array.from(files);
    let roomAtCall = 0;
    setImages((prev) => {
      roomAtCall = Math.max(0, MAX_IMAGES - prev.length);
      if (roomAtCall === 0) {
        toast.warning(`Already at the ${MAX_IMAGES}-image limit — remove some first`);
        return prev;
      }
      if (allowed.length > roomAtCall) {
        toast.warning(`Only adding ${roomAtCall} of ${allowed.length} images — ${MAX_IMAGES} max`);
      }
      return prev;
    });
    if (roomAtCall === 0) return;
    const reads = allowed.slice(0, roomAtCall).map(
      (f) => new Promise((resolve) => {
        if (f.size > 12 * 1024 * 1024) { toast.error(`${f.name} is over 12MB — skipped`); resolve(null); return; }
        const r = new FileReader();
        r.onload = () => resolve({ id: Math.random().toString(36).slice(2, 9), dataUrl: String(r.result || ""), name: f.name });
        r.onerror = () => resolve(null);
        r.readAsDataURL(f);
      })
    );
    Promise.all(reads).then((imgs) => {
      const valid = imgs.filter(Boolean);
      setImages((prev) => [...prev, ...valid].slice(0, MAX_IMAGES));
    });
  };

  // Paste-from-clipboard support
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      const files = [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) { addFiles(files); e.preventDefault(); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  // Feb 2026 — extractor mode: "accounts" (default credit-report),
  // "inquiry" (hard inquiries only), or "chexsystems" (banking-report
  // screenshots — extracts only Source of Information + Date of
  // Closure + Original Charge Off Amount).
  const [extractMode, setExtractMode] = useState("accounts");

  // Per user spec 2026-06-05: each Extract Accounts click starts a FRESH
  // extraction. If the user wants to merge new results with what's already on
  // screen, they click "Add to Existing List" instead. addMode === true skips
  // the reset and merges into the current list.
  const runExtract = async ({ addMode = false } = {}) => {
    const trimmedText = text.trim();
    if (!images.length && !trimmedText) { toast.error("Add a screenshot or paste text first"); return; }
    setBusy(true);
    if (!addMode) setAccounts([]);
    try {
      // Feb 2026: local regex parser removed from this step — it was
      // misfiring on Credit Karma / Experian / TransUnion / Equifax web
      // copy (treating column headers like "Utilization" or "Balance"
      // as account names).  This step is now ALWAYS AI-powered — Gemini
      // handles every format reliably.  The local parser is still used
      // on Step 2 for well-structured text.

      // ── AI path: images, PDFs, or ANY text. ──
      // Feb 2026: rewired to the Gemini-powered /api/account-extract
      // endpoint that reliably handles Credit Karma / Experian /
      // TransUnion / Equifax formats out of the box.  We batch images
      // in groups of 6 so any single Gemini call stays well under its
      // token limit; the ExtractedAccount → FTC-schema translation
      // happens client-side (see ASB_TO_FTC_CATEGORY below).
      const ASB_TO_FTC_CATEGORY = {
        "Credit Card": "CREDIT_CARD",
        "Retail Card": "CREDIT_CARD",
        "Auto Loan": "AUTO",
        "Repossession": "AUTO",
        "Personal Loan": "PERSONAL_LOAN",
        "Installment Loan": "PERSONAL_LOAN",
        "Buy Now Pay Later": "PERSONAL_LOAN",
        "Mortgage": "MORTGAGE",
        // 2026-02-24 (user request) — first-class Apartment / Rental lease categories.
        "Apartment Lease": "APARTMENT_LEASE",
        "Rental Lease": "RENTAL_LEASE",
        "Student Loan": "STUDENT_LOAN",
        "Telecom": "UTILITY",
        "Utility": "UTILITY",
        "Medical": "MEDICAL",
        "Child Support": "OTHER",
        "Timeshare": "OTHER",
        "Collection": "COLLECTION",
        "Other": "OTHER",
      };
      const dataUrlToBlob = (dataUrl) => {
        const [meta, b64] = dataUrl.split(",");
        const mime = /data:(.*);base64/.exec(meta || "")?.[1] || "image/png";
        const bin = atob(b64 || "");
        const arr = new Uint8Array(bin.length);
        for (let k = 0; k < bin.length; k++) arr[k] = bin.charCodeAt(k);
        return new Blob([arr], { type: mime });
      };
      const extToName = (mime) => {
        const map = { "image/jpeg": ".jpg", "image/png": ".png", "image/webp": ".webp", "image/heic": ".heic", "application/pdf": ".pdf" };
        return map[mime] || ".png";
      };

      const chunks = [];
      if (images.length === 0) {
        chunks.push({ imgs: [], text: trimmedText });
      } else {
        for (let i = 0; i < images.length; i += BATCH_SIZE) {
          chunks.push({
            imgs: images.slice(i, i + BATCH_SIZE),
            text: i === 0 ? trimmedText : "",
          });
        }
      }
      setBatchProgress({ done: 0, total: chunks.length });

      let merged = addMode ? [...accounts] : [];
      for (let i = 0; i < chunks.length; i++) {
        try {
          const fd = new FormData();
          if (chunks[i].text) fd.append("pastedText", chunks[i].text);
          // Feb 2026 — propagate the extractor mode (accounts /
          // inquiry / chexsystems).  Falls back to "accounts" server-side
          // when omitted, keeping older callers unchanged.
          if (extractMode) fd.append("mode", extractMode);
          chunks[i].imgs.forEach((img, k) => {
            const blob = dataUrlToBlob(img.dataUrl);
            fd.append(
              "files",
              blob,
              img.name || `screenshot_${i}_${k}${extToName(blob.type)}`,
            );
          });
          // Kick off an async extraction job so we bypass the Cloudflare / K8s
          // ingress ~60s idle timeout that caused sporadic errors when Gemini
          // took longer on a large batch.  We upload once, then cheaply poll
          // for the result — same pattern the standalone ASB page uses.
          const startResp = await axios.post(
            `${API}/api/account-extract/start`, fd,
            { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 },
          );
          const jobId = startResp.data?.jobId;
          if (!jobId) throw new Error("Extraction did not return a job id.");

          const startedAt = Date.now();
          const MAX_MS = 6 * 60 * 1000;
          let r = null;
          while (true) {
            await new Promise((res) => setTimeout(res, 2000));
            let pollResp;
            try {
              pollResp = await axios.get(
                `${API}/api/account-extract/status/${jobId}`,
                { timeout: 20000 },
              );
            } catch (pollErr) {
              // Transient hiccup — retry on next tick until MAX_MS.
              if (Date.now() - startedAt > MAX_MS) throw pollErr;
              continue;
            }
            const pd = pollResp.data;
            if (pd?.status === "done") { r = { data: pd }; break; }
            if (pd?.status === "error") throw new Error(pd.error || "Extraction failed.");
            if (Date.now() - startedAt > MAX_MS) {
              throw new Error("Extraction is taking too long — try smaller / fewer files.");
            }
          }
          // Translate ASB's 16-category schema → FTC's 10-category schema
          // and rename the fields to what the existing UI + Step 2 handoff
          // already expect.
          const translated = (r.data?.accounts || []).map((a) => ({
            id: Math.random().toString(36).slice(2, 12),
            account_name: String(a.accountName || "").toUpperCase().trim(),
            date_opened: a.openDate || "",
            balance: a.balance === null || a.balance === undefined ? 0 : Number(a.balance),
            category: ASB_TO_FTC_CATEGORY[a.accountType] || "OTHER",
            previous_creditor:
              (ASB_TO_FTC_CATEGORY[a.accountType] || "OTHER") === "COLLECTION"
                ? (a.originalCreditor || "Not Provided")
                : null,
            // Preserve confidence so the UI can flag low-confidence rows.
            _confidence: a.confidence || {},
          }));
          merged = mergeAccounts(merged, translated);
          setAccounts(merged);
        } catch (e) {
          toast.error(`Batch ${i + 1}/${chunks.length} failed: ${e?.response?.data?.detail || e.message}`);
        }
        setBatchProgress({ done: i + 1, total: chunks.length });
      }

      if (merged.length === 0) toast.warning("No accounts detected — try clearer screenshots or paste plain text.");
      else toast.success(`Found ${merged.length} account${merged.length === 1 ? "" : "s"} across ${chunks.length} batch${chunks.length === 1 ? "" : "es"}`);
      // Always clear the input panel after a successful extract so the next
      // "Add to existing" click re-opens an empty panel instead of silently
      // re-extracting stale text/images.
      if (merged.length > 0) {
        setImages([]);
        setText("");
        setShowAddMore(false);
      }
    } finally {
      setBusy(false);
      setBatchProgress({ done: 0, total: 0 });
    }
  };

  // Deduplicate across batches — same account_name + same date_opened = same row.
  // Also guarantee a unique id for every account so React keys never collide.
  function mergeAccounts(existing, incoming) {
    const key = (a) => `${(a.account_name || "").toUpperCase().trim()}|${(a.date_opened || "").trim()}`;
    const seen = new Map(existing.map((a) => [key(a), a]));
    const usedIds = new Set(existing.map((a) => a.id).filter(Boolean));
    for (const a of incoming) {
      const k = key(a);
      if (seen.has(k)) continue;
      let id = a.id;
      if (!id || usedIds.has(id)) id = Math.random().toString(36).slice(2, 12);
      usedIds.add(id);
      seen.set(k, { ...a, id });
    }
    // Re-sort by category order + date asc (matches server-side _sort_accounts)
    const order = CATEGORY_ORDER;
    const idx = (c) => Math.max(0, order.indexOf(c));
    return Array.from(seen.values()).sort((a, b) => {
      const d = idx(a.category) - idx(b.category);
      if (d !== 0) return d;
      // Convert MM-DD-YYYY → YYYY-MM-DD for lex compare
      const norm = (s) => (s && /^(\d{2})-(\d{2})-(\d{4})$/.test(s) ? `${RegExp.$3}-${RegExp.$1}-${RegExp.$2}` : (s || "9999-99-99"));
      return norm(a.date_opened).localeCompare(norm(b.date_opened));
    });
  }

  const updateAccount = (id, patch) =>
    setAccounts((arr) => arr.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const deleteAccount = (id) =>
    setAccounts((arr) => arr.filter((a) => a.id !== id));
  const addBlankAccount = (category) =>
    setAccounts((arr) => [...arr, {
      id: Math.random().toString(36).slice(2, 12),
      account_name: "", date_opened: "", balance: 0,
      category, previous_creditor: category === "COLLECTION" ? "Not Provided" : null,
    }]);

  // Group + sort for display
  // Feb 2026 (user request): when multiple accounts share the same
  // creditor name (e.g. two DISCOVER rows), keep them adjacent so they
  // can be added to the same FTC section together.  Primary sort is by
  // a normalized creditor name (lowercased, punctuation-stripped, first
  // token only so "Discover Card" and "DISCOVER BANK" cluster); ties
  // break by date_opened for stable ordering.
  const _normalizeCreditor = (n) => {
    const s = String(n || "").toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();
    if (!s) return "\uffff";
    // Use first non-generic token so brand variants stay together.
    const skip = new Set(["the", "a", "an"]);
    for (const tok of s.split(/\s+/)) {
      if (!skip.has(tok)) return tok;
    }
    return s;
  };
  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: accounts
      .filter((a) => a.category === cat)
      .sort((a, b) => {
        const nA = _normalizeCreditor(a.account_name);
        const nB = _normalizeCreditor(b.account_name);
        if (nA !== nB) return nA.localeCompare(nB);
        return (a.date_opened || "9999").localeCompare(b.date_opened || "9999");
      }),
  })).filter((g) => g.items.length > 0);

  // ── Shorten & Merge shim ─────────────────────────────────────────────
  // The AccountAbbreviationModal was built for the paste-accounts flow
  // (which stores rows as { creditorName, fraudAmount, dateOpened }).
  // Here the extractor stores rows as { account_name, balance,
  // date_opened }, so we translate in and back out around the modal.
  const abbrevAccounts = accounts.map((a) => ({
    id: a.id,
    creditorName: a.account_name || "",
    originalName: a.account_name || "",
    fraudAmount: a.balance ? `$${a.balance}` : "",
    dateOpened: a.date_opened || "",
    category: a.category,
    previous_creditor: a.previous_creditor || null,
    _confidence: a._confidence || {},
  }));
  const handleAbbrevApply = (transformed) => {
    // Convert the modal's output back to the extractor's schema, dropping
    // the modal-only keys (short, displayName, _mergedFromIds, etc.) that
    // the rest of this component doesn't need.
    const parseDollar = (v) => {
      if (v === null || v === undefined) return 0;
      const raw = String(v).replace(/[$,\s]/g, "");
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    };
    const next = transformed.map((r) => ({
      id: r.id || Math.random().toString(36).slice(2, 12),
      account_name: (r.displayName || r.creditorName || r.originalName || "").toUpperCase().trim(),
      date_opened: r.dateOpened || "",
      balance: parseDollar(r.fraudAmount ?? r.balance),
      category: r.category || "OTHER",
      previous_creditor: r.previous_creditor || null,
      _confidence: r._confidence || {},
      _mergedCount: r._mergedCount || undefined,
    }));
    setAccounts(next);
    toast.success(`${next.length} account${next.length === 1 ? '' : 's'} after shorten & merge`);
  };

  // ── Final formatted text per the user's "Copy All" spec ─────────────────
  const buildFinalOutput = () => {
    const lines = [];
    // Default headings — used unless the user has renamed the section. The
    // user-facing label is converted to its "ALL CAPS ACCOUNTS" form by
    // uppercasing and (if needed) suffixing the word "ACCOUNTS".
    const defaultHeading = {
      AUTO: "AUTO ACCOUNTS",
      CREDIT_CARD: "CREDIT CARD ACCOUNTS",
      CREDIT_UNION: "CREDIT UNION ACCOUNTS",
      PERSONAL_LOAN: "PERSONAL LOAN ACCOUNTS",
      STUDENT_LOAN: "STUDENT LOAN ACCOUNTS",
      MORTGAGE: "MORTGAGE ACCOUNTS",
      COLLECTION: "COLLECTION ACCOUNTS",
      OTHER: "OTHER ACCOUNTS",
    };
    const headingFor = (cat) => {
      const custom = labelOverrides[cat];
      if (custom) {
        const upper = custom.toUpperCase().trim();
        return /\bACCOUNTS?\b/.test(upper) ? upper : `${upper} ACCOUNTS`;
      }
      return defaultHeading[cat] || `${cat} ACCOUNTS`;
    };
    for (const cat of CATEGORY_ORDER) {
      const items = accounts.filter((a) => a.category === cat);
      if (!items.length) continue;
      lines.push(headingFor(cat));
      lines.push("");
      for (const a of items) {
        const bal = `$${Number(a.balance || 0).toLocaleString("en-US")}`;
        lines.push(`${a.account_name} – ${a.date_opened || ""} – ${bal}`);
        if (cat === "COLLECTION") lines.push(`Previous Creditor: ${a.previous_creditor || "Not Provided"}`);
        lines.push("");
      }
    }
    return lines.join("\n").trim();
  };

  const copyAll = async () => {
    const out = buildFinalOutput();
    if (!out) { toast.error("Nothing to copy yet"); return; }
    try {
      await navigator.clipboard.writeText(out);
      toast.success("Copied final list to clipboard");
    } catch {
      toast.error("Clipboard blocked — long-press and copy manually");
    }
  };

  const clearAll = () => {
    if (!window.confirm("Clear the current account list?")) return;
    setAccounts([]);
    setImages([]);
    setText("");
    toast.success("Cleared");
  };

  const [showAddMore, setShowAddMore] = useState(false);
  // Currently-dragged account id, used to highlight the drop target.
  const [dragId, setDragId] = useState(null);
  const [dragOverCat, setDragOverCat] = useState(null);

  const moveAccountToCategory = (id, newCategory) => {
    setAccounts((prev) => prev.map((a) => {
      if (a.id !== id) return a;
      const update = { ...a, category: newCategory };
      // Only collections track a previous creditor; clear it on moves out.
      if (newCategory === "COLLECTION") {
        update.previous_creditor = a.previous_creditor || "Not Provided";
      } else {
        update.previous_creditor = null;
      }
      return update;
    }));
  };

  const useAccounts = () => {
    if (accounts.length === 0) { toast.error("Extract or add at least one account first"); return; }
    // Per user request 2026-06-05: emit category HEADERS in the handoff text
    // so the Paste Accounts screen looks identical to the AI extractor —
    // section name + count, then accounts under it. The Step 2 parser is
    // tolerant of these labels (it strips header-only lines before parsing).
    const labels = {
      AUTO: "AUTO ACCOUNTS",
      CREDIT_CARD: "CREDIT CARD ACCOUNTS",
      CREDIT_UNION: "CREDIT UNION ACCOUNTS",
      PERSONAL_LOAN: "PERSONAL LOAN ACCOUNTS",
      STUDENT_LOAN: "STUDENT LOAN ACCOUNTS",
      MORTGAGE: "MORTGAGE ACCOUNTS",
      UTILITY: "UTILITY ACCOUNTS",
      MEDICAL: "MEDICAL ACCOUNTS",
      COLLECTION: "COLLECTION ACCOUNTS",
      OTHER: "OTHER ACCOUNTS",
    };
    const lines = [];
    for (const cat of CATEGORY_ORDER) {
      const items = accounts.filter((a) => a.category === cat);
      if (!items.length) continue;
      // Honor any user-renamed section heading first.
      const custom = labelOverrides[cat];
      const heading = (custom
        ? (custom.toUpperCase().includes("ACCOUNT") ? custom.toUpperCase() : `${custom.toUpperCase()} ACCOUNTS`)
        : labels[cat]);
      lines.push(`${heading} (${items.length})`);
      for (const a of items) {
        const amt = `$${Number(a.balance || 0).toLocaleString("en-US")}`;
        let date = a.date_opened || "";
        const m = date.match(/^(\d{2})-\d{2}-(\d{4})$/);
        if (m) date = `${m[1]}-${m[2]}`;
        const name = (a.account_name || "").toUpperCase().trim();
        if (!name) continue;
        lines.push(date ? `${name} - ${date} - ${amt}` : `${name} - ${amt}`);
        if (cat === "COLLECTION" && a.previous_creditor) {
          lines.push(`Previous Creditor: ${a.previous_creditor}`);
        }
      }
      lines.push(""); // blank line between groups
    }
    onUseAccounts({ text: lines.join("\n").trim(), accounts });
  };

  const hasResults = accounts.length > 0;

  return (
    <Card className="border-zinc-700">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              Step 1½: AI Account Extractor <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/40">Optional</span>
            </CardTitle>
            <CardDescription>
              Upload screenshots from Credit Karma, Experian, mortgage credit reports — or paste raw text. The AI will pull
              each account, categorize it, and apply short identifiable abbreviations (CAP 1, NAVY FED, JPMCB, 1MAIN…). You can edit everything
              before continuing.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={onSkip}
            data-testid="extractor-skip-btn"
            className="bg-zinc-900 border-zinc-600 hover:bg-zinc-800 text-zinc-200"
          >
            <SkipForward className="w-4 h-4 mr-1" /> Skip — paste manually
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Mode picker — Feb 2026.  Switches the Gemini prompt so the
            extractor knows what kind of screenshots you're feeding it.
            The default "Credit Report" mode is the classic behavior;
            "ChexSystems" mode extracts only Source of Information +
            Date of Closure + Original Charge Off Amount from banking
            report screenshots. */}
        {(!hasResults || showAddMore) && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">Report type:</span>
            {[
              { v: "accounts",    label: "Credit Report",       hint: "Credit Karma, Experian, mortgage tri-merge" },
              { v: "inquiry",     label: "Hard Inquiries",      hint: "Only unauthorized credit inquiries" },
              { v: "chexsystems", label: "ChexSystems",         hint: "Bank-report screenshots — 3 fields only" },
            ].map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setExtractMode(m.v)}
                title={m.hint}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                  extractMode === m.v
                    ? (m.v === 'chexsystems'
                        ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30'
                        : m.v === 'inquiry'
                          ? 'bg-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30'
                          : 'bg-violet-500 text-white shadow-lg shadow-violet-500/30')
                    : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700'
                }`}
                data-testid={`extractor-mode-${m.v}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        {/* Source inputs — visible when no results yet, OR when the user
            clicked "Add to existing" and wants to paste more. */}
        {(!hasResults || showAddMore) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Screenshot dropzone */}
            <div>
              <label className="text-sm text-zinc-300 flex items-center gap-1 mb-2">
                <ImageIcon className="w-4 h-4 text-violet-300" /> Screenshots <span className="text-zinc-500 text-xs">(up to {MAX_IMAGES} — on iPhone tap the box, then select multiple from Photos)</span>
              </label>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-zinc-700 hover:border-violet-500 rounded-xl p-6 text-center cursor-pointer transition"
                data-testid="extractor-dropzone"
              >
                <Upload className="w-6 h-6 mx-auto text-zinc-500 mb-2" />
                <p className="text-sm text-zinc-300 font-medium">Drag screenshots here, click to pick, or paste</p>
                <p className="text-xs text-zinc-500 mt-1">PNG / JPG / WEBP — max 12MB each</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf,.pdf"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="extractor-file-input"
              />
              {images.length > 0 && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {images.map((img) => (
                    <div key={img.id} className="relative group">
                      <img src={img.dataUrl} alt={img.name} className="w-full h-20 object-cover rounded-md border border-zinc-700" />
                      <button
                        onClick={() => setImages((p) => p.filter((x) => x.id !== img.id))}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        data-testid={`extractor-image-remove-${img.id}`}
                      ><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pasted text */}
            <div>
              <label className="text-sm text-zinc-300 flex items-center gap-1 mb-2">
                <FileText className="w-4 h-4 text-violet-300" /> Or paste raw text
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste the accounts section straight from your credit report — the AI handles dates, balances, and abbreviations."
                className="w-full h-44 bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2 text-sm font-mono"
                data-testid="extractor-text-input"
              />
            </div>
          </div>
        )}

        {/* Extract button — visible when no results yet, OR when adding more */}
        {(!hasResults || showAddMore) && (
          <Button
            onClick={() => runExtract({ addMode: hasResults })}
            disabled={busy || (images.length === 0 && !text.trim())}
            className="w-full bg-violet-600 hover:bg-violet-700"
            data-testid="extractor-run-btn"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {batchProgress.total > 1
                  ? `Extracting batch ${batchProgress.done + 1}/${batchProgress.total}…`
                  : "Extracting…"}
              </>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" />
                {hasResults ? "Add to list" : "Extract Accounts"}
                {images.length > BATCH_SIZE ? ` (${Math.ceil(images.length / BATCH_SIZE)} batches)` : ""}
              </>
            )}
          </Button>
        )}

        {/* Results editor — grouped by category */}
        {hasResults && (
          <div className="space-y-5">
            {/* Feb 2026 — dedicated bulk-edit banner. The user pointed
                out the small "Shorten & Merge" pill in the toolbar was
                easy to miss; hoisting it into its own gradient banner
                right above the grouped list so it's the first thing
                you see after extraction. */}
            <div
              className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-purple-500/40 bg-gradient-to-r from-purple-900/30 via-violet-900/20 to-fuchsia-900/20"
              data-testid="extractor-bulk-edit-banner"
            >
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-100">
                  <span className="mr-2">✂️</span>Shorten, merge or bulk-edit these accounts
                </p>
                <p className="text-xs text-purple-200/70 mt-1">
                  Abbreviate long names (Capital One → Cap One), merge duplicates (2 Discover rows → “2 - Discover”), and fine-tune names, dates or amounts before you build the FTC report.
                </p>
              </div>
              <Button
                onClick={() => setAbbrevModalOpen(true)}
                className="bg-purple-600 hover:bg-purple-500 text-white font-semibold shrink-0"
                data-testid="extractor-shorten-merge-btn"
              >
                Open Bulk Editor
              </Button>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-zinc-300">
                <strong className="text-violet-300">{accounts.length}</strong> accounts extracted — drag any row between sections to re-categorize, or use the per-row dropdown. Duplicate creditors are sorted next to each other.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={copyAll} className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="extractor-copy-all-btn">
                  <Copy className="w-3 h-3 mr-1" /> Copy all
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  // If we have new input ready, run extraction in addMode.
                  // Otherwise open the input panel so the user can paste / drop.
                  if (images.length || text.trim()) {
                    runExtract({ addMode: true });
                  } else {
                    setShowAddMore(true);
                    toast.message("Paste text or drop a screenshot below, then click Extract to add.");
                  }
                }} disabled={busy} className="bg-zinc-900 border-emerald-700/40 text-emerald-300" data-testid="extractor-add-to-existing-btn">
                  <PlusCircle className="w-3 h-3 mr-1" /> Add to existing
                </Button>
                <Button variant="outline" size="sm" onClick={clearAll} className="bg-zinc-900 border-red-700/40 text-red-300" data-testid="extractor-clear-all-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Clear list
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setAccounts([]); setImages([]); setText(""); }} className="bg-zinc-900 border-zinc-700 text-zinc-300" data-testid="extractor-restart-btn">
                  <ArrowLeft className="w-3 h-3 mr-1" /> Start over
                </Button>
              </div>
            </div>

            {grouped.map(({ category, items }) => (
              <div
                key={category}
                className={`rounded-xl border bg-zinc-900/40 overflow-hidden transition-all ${dragOverCat === category ? 'border-violet-400 ring-2 ring-violet-400/40' : 'border-zinc-700'}`}
                data-testid={`extractor-group-${category}`}
                onDragOver={(e) => {
                  // Required to make the section a valid drop target.
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (dragOverCat !== category) setDragOverCat(category);
                }}
                onDragLeave={(e) => {
                  // Only clear if we're really leaving the section, not just
                  // crossing into a child row.
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setDragOverCat((cur) => (cur === category ? null : cur));
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/account-id") || dragId;
                  if (id) moveAccountToCategory(id, category);
                  setDragId(null);
                  setDragOverCat(null);
                }}
              >
                <div className="bg-zinc-800/80 px-4 py-2 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2 min-w-0 flex-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-violet-400 flex-shrink-0" />
                    {editingCategoryLabel === category ? (
                      <Input
                        autoFocus
                        defaultValue={labelFor(category)}
                        onBlur={(e) => { renameCategory(category, e.target.value); setEditingCategoryLabel(null); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { renameCategory(category, e.currentTarget.value); setEditingCategoryLabel(null); }
                          if (e.key === "Escape") { setEditingCategoryLabel(null); }
                        }}
                        className="h-7 px-2 py-0 text-sm font-bold bg-zinc-900 border-violet-500 text-white max-w-[260px]"
                        data-testid={`extractor-group-label-input-${category}`}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingCategoryLabel(category)}
                        title="Click to rename this section"
                        className="text-left hover:text-violet-300 transition truncate"
                        data-testid={`extractor-group-label-${category}`}
                      >
                        {labelFor(category)}
                      </button>
                    )}
                    <span className="text-xs text-zinc-400 font-normal flex-shrink-0">({items.length})</span>
                    {editingCategoryLabel !== category && (
                      <button
                        type="button"
                        onClick={() => setEditingCategoryLabel(category)}
                        className="ml-1 p-1 text-zinc-500 hover:text-violet-300 rounded transition opacity-70 hover:opacity-100"
                        title="Rename section"
                        data-testid={`extractor-group-edit-${category}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                    {editingCategoryLabel !== category && labelOverrides[category] && (
                      <button
                        type="button"
                        onClick={() => renameCategory(category, "")}
                        className="text-[10px] text-zinc-500 hover:text-red-400 underline"
                        title="Reset to default name"
                        data-testid={`extractor-group-reset-${category}`}
                      >
                        reset
                      </button>
                    )}
                  </h4>
                  <Button variant="ghost" size="sm" onClick={() => addBlankAccount(category)} className="text-zinc-400 hover:text-white flex-shrink-0" data-testid={`extractor-add-${category}`}>
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                <div className="divide-y divide-zinc-800">
                  {items.map((a) => (
                    <AccountRow
                      key={a.id}
                      acc={a}
                      isEditing={editingId === a.id}
                      onEdit={() => setEditingId(a.id)}
                      onDone={() => setEditingId(null)}
                      onUpdate={(patch) => updateAccount(a.id, patch)}
                      onDelete={() => deleteAccount(a.id)}
                      onMoveCategory={(cat) => moveAccountToCategory(a.id, cat)}
                      onDragStartRow={() => setDragId(a.id)}
                      onDragEndRow={() => { setDragId(null); setDragOverCat(null); }}
                      isDragging={dragId === a.id}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* If none of the categories got items but we have accounts (shouldn't happen) */}
            {grouped.length === 0 && (
              <p className="text-center text-zinc-500 text-sm py-4">No grouped results — try Start Over.</p>
            )}

            <div className="flex flex-col sm:flex-row justify-between gap-2 pt-3 border-t border-zinc-700">
              <Button variant="outline" onClick={onBack} className="bg-zinc-900 border-zinc-700">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={useAccounts} className="bg-emerald-600 hover:bg-emerald-700" data-testid="extractor-use-btn">
                Use these accounts <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {!hasResults && (
          <div className="flex justify-between pt-3 border-t border-zinc-700">
            <Button variant="outline" onClick={onBack} className="bg-zinc-900 border-zinc-700">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        )}
      </CardContent>

      {/* Feb 2026 — Shorten & Merge modal, driven off the same component
          the paste-accounts page uses. Data is translated in and out to
          fit the extractor's { account_name, balance, date_opened } shape. */}
      <AccountAbbreviationModal
        open={abbrevModalOpen}
        onOpenChange={setAbbrevModalOpen}
        accounts={abbrevAccounts}
        onApply={handleAbbrevApply}
      />
    </Card>
  );
}

/**
 * Generate up to 4 friendly abbreviation suggestions for an account name.
 * The user can click any one to swap the displayed creditor name. Per user
 * request 2026-06-05: never collapse a multi-word creditor into a single
 * cryptic acronym like "DOE" — always include at least one mid-length form
 * such as "US DEP EDU" alongside the full name.
 */
function buildNameSuggestions(acc) {
  const raw = (acc.source_name || acc.raw_name || acc.sourceText || acc.account_name || "")
    .toString().replace(/[._]+/g, " ").replace(/\s+/g, " ").trim();
  if (!raw) return [];
  const upper = raw.toUpperCase();

  // Stopwords that almost never carry meaning in a credit-report creditor name
  const STOP = new Set([
    "BANK", "BANCORP", "BANCO", "USA", "NA", "N.A", "LLC", "LP", "INC", "INC.",
    "CORP", "CORPORATION", "CO", "CO.", "COMPANY", "GROUP", "HOLDINGS",
    "FINANCIAL", "FIN", "SERVICES", "SVC", "SVCS", "CONSUMER", "CARD",
    "OF", "THE", "AND", "&", "FOR", "TO", "ON",
  ]);

  // 1) Full uppercase name (truncated to 28 chars so it doesn't blow up the row)
  const fullName = upper.length > 28 ? `${upper.slice(0, 26).trim()}…` : upper;

  // 2) Significant-word truncation: keep the first 2-3 non-stopwords, shortened
  const tokens = upper.replace(/[,\-\/]+/g, " ").split(/\s+/).filter(Boolean);
  const sigTokens = tokens.filter(t => !STOP.has(t.replace(/\./g, "")));

  // Compact form (3-4 letter prefix of each significant word, up to 3 words).
  const compactForm = sigTokens
    .slice(0, 3)
    .map(t => (t.length <= 5 ? t : t.slice(0, 4).replace(/[^A-Z]/g, "")))
    .join(" ")
    .trim();

  // Initials form (only if 3+ significant tokens — otherwise it's just 1-2
  // letters which is too cryptic).
  const initials = sigTokens.length >= 3
    ? sigTokens.slice(0, 4).map(t => t[0]).join("")
    : "";

  const out = [];
  const push = (s) => {
    if (!s) return;
    const v = s.trim().toUpperCase();
    if (!v || out.includes(v)) return;
    out.push(v);
  };
  push(fullName);
  push(compactForm);
  if (initials.length >= 3) push(initials);
  // 4) Always include the current value so the user can see it's "active"
  push(acc.account_name);

  return out.slice(0, 4);
}

function AccountRow({ acc, isEditing, onEdit, onDone, onUpdate, onDelete, onMoveCategory, onDragStartRow, onDragEndRow, isDragging }) {
  const isCollection = acc.category === "COLLECTION";
  return (
    <div
      className={`px-4 py-2 transition-opacity ${isDragging ? 'opacity-40' : ''}`}
      data-testid={`extractor-row-${acc.id}`}
      draggable={!isEditing}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/account-id", acc.id);
        if (onDragStartRow) onDragStartRow();
      }}
      onDragEnd={() => { if (onDragEndRow) onDragEndRow(); }}
    >
      {isEditing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-12 sm:col-span-5">
              <Input
                value={acc.account_name}
                onChange={(e) => onUpdate({ account_name: e.target.value.toUpperCase() })}
                placeholder="ACCOUNT NAME"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid={`extractor-edit-name-${acc.id}`}
              />
              {/* Friendly abbreviation chips — generated from the original
                  source text. Click any chip to swap the name. The first
                  suggestion is "Use full name" so the user can always escape
                  an over-aggressive abbreviation. */}
              {(() => {
                const suggestions = buildNameSuggestions(acc);
                if (!suggestions.length) return null;
                return (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => onUpdate({ account_name: s })}
                        className={`px-2 py-0.5 text-[10px] rounded-md border transition-colors ${acc.account_name === s ? 'bg-violet-500/20 border-violet-400 text-violet-200' : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-violet-500/50'}`}
                        data-testid={`extractor-name-suggestion-${acc.id}-${i}`}
                        title="Click to use this name"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            <Input
              value={acc.date_opened || ""}
              onChange={(e) => onUpdate({ date_opened: e.target.value })}
              placeholder="MM-DD-YYYY"
              className="col-span-6 sm:col-span-3 bg-zinc-800 border-zinc-700 text-white"
              data-testid={`extractor-edit-date-${acc.id}`}
            />
            <Input
              type="text"
              value={acc.balance ?? ""}
              onChange={(e) => onUpdate({ balance: parseFloat(String(e.target.value).replace(/[^0-9.]/g, "")) || 0 })}
              placeholder="0"
              className="col-span-6 sm:col-span-2 bg-zinc-800 border-zinc-700 text-emerald-300"
              data-testid={`extractor-edit-amount-${acc.id}`}
            />
            <select
              value={acc.category}
              onChange={(e) => onUpdate({ category: e.target.value, previous_creditor: e.target.value === "COLLECTION" ? (acc.previous_creditor || "Not Provided") : null })}
              className="col-span-12 sm:col-span-2 bg-zinc-800 border border-zinc-700 text-white rounded-md px-2 py-1 text-sm"
              data-testid={`extractor-edit-category-${acc.id}`}
            >
              {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
          </div>
          {isCollection && (
            <Input
              value={acc.previous_creditor || ""}
              onChange={(e) => onUpdate({ previous_creditor: e.target.value })}
              placeholder="Previous creditor (optional)"
              className="bg-zinc-800 border-zinc-700 text-zinc-200 text-sm"
              data-testid={`extractor-edit-prev-${acc.id}`}
            />
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={onDone} className="bg-violet-600 hover:bg-violet-700">Done</Button>
            <Button size="sm" variant="outline" onClick={onDelete} className="bg-zinc-900 border-zinc-700 text-red-400" data-testid={`extractor-delete-${acc.id}`}>
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            {/* Drag-handle / grip icon — visual cue that the row is draggable */}
            <span
              className="text-zinc-600 hover:text-zinc-300 cursor-grab active:cursor-grabbing select-none flex-shrink-0"
              title="Drag to move to another section"
              data-testid={`extractor-drag-handle-${acc.id}`}
            >
              <GripVertical className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white text-sm truncate">
                {acc.account_name || <span className="text-zinc-500 italic">Untitled</span>}
                {acc.date_opened && <span className="text-zinc-400 font-normal"> — {acc.date_opened}</span>}
                {" "}
                <span className="text-emerald-300">— ${Number(acc.balance || 0).toLocaleString()}</span>
              </p>
              {isCollection && acc.previous_creditor && (
                <p className="text-xs text-zinc-500 italic mt-0.5">Previous Creditor: {acc.previous_creditor}</p>
              )}
            </div>
            {/* Always-visible category quick-switch dropdown so the user can
                move this account to another section without entering edit mode. */}
            <select
              value={acc.category}
              onChange={(e) => onMoveCategory && onMoveCategory(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-800 border border-zinc-700 text-violet-300 rounded-md px-2 py-1 text-xs max-w-[110px] flex-shrink-0"
              title="Move to a different section"
              data-testid={`extractor-row-category-${acc.id}`}
            >
              {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
            <button onClick={onEdit} className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded" data-testid={`extractor-edit-${acc.id}`}>
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={onDelete} className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Always-visible abbreviation dropdown — pick a shorter form for the
              account name without entering edit mode. Per user request
              2026-06-05: full name is the default, dropdown lets you trim it. */}
          {(() => {
            const opts = buildNameSuggestions(acc);
            if (opts.length <= 1) return null;
            return (
              <div className="flex items-center gap-1.5 pl-6">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Abbrev:</span>
                <select
                  value={acc.account_name}
                  onChange={(e) => onUpdate({ account_name: e.target.value })}
                  className="bg-zinc-900 border border-zinc-700 text-zinc-200 rounded px-2 py-0.5 text-[11px] max-w-[280px] truncate"
                  data-testid={`extractor-row-abbrev-${acc.id}`}
                  title="Pick a different abbreviation"
                >
                  {opts.map((o, i) => (
                    <option key={`${o}-${i}`} value={o}>{o}</option>
                  ))}
                  {/* If the current name isn't in the suggestion list (the user
                      typed a custom one) make sure it stays selected. */}
                  {!opts.includes(acc.account_name) && acc.account_name && (
                    <option value={acc.account_name}>{acc.account_name} (custom)</option>
                  )}
                </select>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
